package diana.soleil.design.iraniancanadienbazzar

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareTarget
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ShareSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ShareSheetTarget
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import iraniancanadienbazzar.shared.generated.resources.Res
import iraniancanadienbazzar.shared.generated.resources.share_copy_link
import iraniancanadienbazzar.shared.generated.resources.share_instagram_hint
import iraniancanadienbazzar.shared.generated.resources.share_more_options
import iraniancanadienbazzar.shared.generated.resources.share_sheet_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

/**
 * App-shell host for the Mahaleh share sheet: every feature's share button funnels through
 * `ShareService.share(text)`, and this single overlay turns it into the community-first sheet —
 * Telegram/WhatsApp/Instagram/Facebook pinned up top (owner decision 2026-07-18), then copy-link
 * and the OS chooser as "More". Instagram has no pre-filled share URL, so its tap copies the link
 * first and opens Instagram to paste (the sheet's footnote says so).
 */
@Composable
fun ShareSheetHost() {
    val shareService = koinInject<ShareService>()
    val pending by shareService.pendingShare.collectAsState()
    val text = pending ?: return
    val clipboard = LocalClipboardManager.current

    fun copyLink() {
        clipboard.setText(AnnotatedString(shareService.extractUrl(text) ?: text))
    }

    ShareSheet(
        title = stringResource(Res.string.share_sheet_title),
        targets = listOf(
            ShareSheetTarget(ShareTarget.Telegram.displayName, AppTheme.colors.brandTelegram) {
                shareService.shareTo(ShareTarget.Telegram, text)
                shareService.clearPending()
            },
            ShareSheetTarget(ShareTarget.WhatsApp.displayName, AppTheme.colors.brandWhatsApp) {
                shareService.shareTo(ShareTarget.WhatsApp, text)
                shareService.clearPending()
            },
            ShareSheetTarget(ShareTarget.Instagram.displayName, AppTheme.colors.brandInstagram) {
                copyLink()
                shareService.shareTo(ShareTarget.Instagram, text)
                shareService.clearPending()
            },
            ShareSheetTarget(ShareTarget.Facebook.displayName, AppTheme.colors.brandFacebook) {
                shareService.shareTo(ShareTarget.Facebook, text)
                shareService.clearPending()
            },
        ),
        footnote = stringResource(Res.string.share_instagram_hint),
        copyLinkLabel = stringResource(Res.string.share_copy_link),
        onCopyLink = {
            copyLink()
            shareService.clearPending()
        },
        moreLabel = stringResource(Res.string.share_more_options),
        onMore = {
            shareService.systemShare(text)
            shareService.clearPending()
        },
        onDismiss = shareService::clearPending,
    )
}
